import classNames from "classnames";
import { Button } from "primereact/button";
import { ChangeEvent, SyntheticEvent, useRef, useState } from "react";
import ImageViewer, {
  reCreateImagePreviewIfBlob,
} from "../image-viewer/image-viewer";

export const evaluateUrlImageIsPreviewable = (
  specimen: string | File | undefined
) => {
  if (typeof specimen === "string") {
    return true;
  }

  return false;
};

export interface FormImageProps {
  urlImage?: string | File;
  label?: string;
  disabled?: boolean;
  imagePreviewClass?: string;
  onImageChange?: (image: File | undefined) => void;
  isCircular?: boolean;
  instruction?: string;
}

export function ImageField({
  label,
  urlImage,
  disabled,
  imagePreviewClass = "w-32 h-32",
  onImageChange,
  isCircular,
  instruction,
}: FormImageProps) {
  const [imageFilePreview, setImageFilePreview] = useState<
    string | File | undefined
  >(urlImage);
  const [onDemandUpload, setOnDemandUpload] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;

    const image = event?.target?.files[0];
    setImageFilePreview(image);

    if (onImageChange) {
      onImageChange(image);
    }
  };

  const uploadMode = (event: SyntheticEvent) => {
    event.preventDefault();
    setOnDemandUpload(true);
  };

  const handleRemoveImage = (event: SyntheticEvent) => {
    if (fileInputRef.current) {
      setImageFilePreview(undefined);
      fileInputRef.current.value = "";
    }

    if (onImageChange) {
      onImageChange(undefined);
    }
  };

  const imageInput = (
    <span id="image-input" className="flex flex-col items-center">
      {imageFilePreview ? (
        <ImageViewer
          className={classNames(imagePreviewClass, isCircular ? "rounded-full object-cover" : "", "mb-2")}
          image={imageFilePreview}
        />
      ) : (
        <div 
          className={classNames(
            "flex flex-col items-center justify-center bg-gray-50 border-2 border-dashed border-gray-300 cursor-pointer hover:bg-gray-100 transition-colors mb-2 text-center p-2",
            isCircular ? "rounded-full" : "rounded-md",
            imagePreviewClass
          )}
          onClick={() => fileInputRef.current?.click()}
        >
          <i className="pi pi-camera text-2xl text-gray-400 mb-1"></i>
          {instruction && <span className="text-xs text-gray-500 leading-tight">{instruction}</span>}
        </div>
      )}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleImageChange}
        disabled={disabled}
        className="hidden"
      />
      {imageFilePreview && (
        <div>
          <Button
            label="Remove Photo"
            onClick={handleRemoveImage}
            severity="secondary"
            size="small"
            outlined
          />
        </div>
      )}
    </span>
  );
  const imagePreview = (
    <span id="image-preview" className="flex flex-col items-center">
      <img
        src={urlImage as string}
        alt={urlImage as string}
        className={classNames(imagePreviewClass, isCircular ? "rounded-full object-cover" : "")}
      />
      <div className="mt-2 flex gap-2">
        <Button onClick={uploadMode} size="small" disabled={disabled} outlined>
          Change
        </Button>
        <Button
          onClick={(e) => {
            e.preventDefault();
            window.open(urlImage as string);
          }}
          size="small"
          disabled={disabled}
          severity="secondary"
          outlined
        >
          View
        </Button>
      </div>
    </span>
  );
  const display = () => {
    if (evaluateUrlImageIsPreviewable(urlImage) && !onDemandUpload) {
      return imagePreview;
    }

    return imageInput;
  };

  return (
    <div>
      {label && <label className="text-gray-500 mb-2">{label}</label>}
      <div>{display()}</div>
    </div>
  );
}

export default ImageField;
